import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { NoteType } from '@shared-models';
import { AlertService } from '@shared-services';
import { round2, roundingTolerance } from '@shared-utils';

import { NoteItemForm } from '../models/note-item-form.model';

@Injectable()
export class NoteValidationService {
  private readonly translate = inject(TranslateService);
  private readonly alertService = inject(AlertService);

  validateCreditNoteLimit(
    noteType: NoteType | undefined,
    invoiceTotal: number,
    currentTotalAccredited: number,
    newNoteTotal: number,
    formatCurrency: (amount: number) => string,
    lineCount = 1,
  ): boolean {
    if (noteType !== 'credit') return true;

    const invoice = round2(invoiceTotal);
    const totalAccredited = round2(Math.abs(currentTotalAccredited));
    const noteTotal = round2(Math.abs(newNoteTotal));
    const tolerance = roundingTolerance(lineCount);

    if (totalAccredited >= invoice) {
      this.alertService.open({
        title: this.translate.instant('global.notes.error'),
        message: this.translate.instant(
          'global.notes.error-max-accredited-reached',
        ),
        type: 'error',
        showClose: true,
      });
      return false;
    }

    if (totalAccredited + noteTotal > invoice + tolerance) {
      const remaining = invoice - totalAccredited;
      this.alertService.open({
        title: this.translate.instant('global.notes.error'),
        message: this.translate.instant(
          'global.notes.error-exceeds-invoice-total',
          {
            remaining: formatCurrency(remaining),
            invoiceTotal: formatCurrency(invoice),
            attempted: formatCurrency(noteTotal),
          },
        ),
        type: 'error',
        showClose: true,
      });
      return false;
    }

    return true;
  }

  hasAnyAdjustment(controls: FormGroup<NoteItemForm>[]): boolean {
    return controls.some((g) => (Number(g.controls.quantity.value) || 0) > 0);
  }
}
